import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleXmark } from "@fortawesome/pro-light-svg-icons"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Checkbox,
    Drawer,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    Typography,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { ReactNode } from "react"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"

const useStyles = makeStyles()((theme) => ({
    container: {
        width: "25rem",
        padding: "2.5rem",
        gap: "1rem",
    },
    // Lift the drawer to dialog level so it sits above a full-screen dialog (e.g. the production view)
    raised: {
        zIndex: theme.zIndex.modal,
    },
    closeButton: {
        display: "block",
        marginLeft: "auto",
        padding: 0,
    },
    iconColor: {
        color: theme.palette.tailwind.slate[400],
    },
    button: {
        color: "#2196f3",
        padding: 0,
        textTransform: "none",
    },
    counter: {
        color: "white",
        lineHeight: 1,
        marginRight: "0.75rem",
        background: theme.palette.tailwind.slate[900],
        borderRadius: "0.25rem",
        padding: "0.25rem",
    },
    accordion: {
        boxShadow: "none",
        "&:before": {
            display: "none",
        },
        "&.Mui-expanded": {
            margin: 0,
        },
    },
    accordionSummary: {
        flexDirection: "row-reverse",
        padding: 0,
        minHeight: "auto",
        "& .MuiAccordionSummary-content": {
            gap: theme.spacing(1),
            alignItems: "center",
            justifyContent: "space-between",
            margin: `${theme.spacing(1)} 0`,
            marginLeft: theme.spacing(1.5),
            "&.Mui-expanded": {
                margin: "0.5rem 0 0.5rem 0.75rem",
            },
        },
        "&.Mui-expanded": {
            minHeight: "auto",
        },
    },
    accordionTitle: {
        color: theme.palette.tailwind.slate[700],
    },
    accordionDetails: {
        padding: "1rem 0",
        color: theme.palette.tailwind.slate[600],
    },
    checkbox: {
        color: theme.palette.tailwind.slate[400],
        "&.Mui-checked": {
            color: "#2196F3",
        },
    },
}))

type FilterMenuProps = {
    drawerOpen: boolean
    handleClose: () => void
    handleClearAll: () => void
    isShowClearAllButton: boolean
    children: ReactNode
    // Render above a full-screen dialog (default drawer z-index sits below dialogs).
    raised?: boolean
}

export function FilterMenu({
    drawerOpen,
    handleClose,
    handleClearAll,
    isShowClearAllButton,
    children,
    raised,
}: FilterMenuProps) {
    const { classes, cx } = useStyles()

    return (
        <Drawer anchor="right" open={drawerOpen} onClose={handleClose} className={cx(raised && classes.raised)}>
            <Grid container flexDirection="column" className={classes.container}>
                <IconButton onClick={handleClose} className={cx(classes.closeButton, classes.iconColor)}>
                    <FontAwesomeIcon icon={faCircleXmark} fontSize={25} />
                </IconButton>

                <Grid container gap={4} flexDirection="column">
                    <Grid container justifyContent="space-between" alignItems="center">
                        <LocalizedStrict id="filter-menu-title">
                            <Typography variant="h5" fontWeight={500}>
                                Filters
                            </Typography>
                        </LocalizedStrict>
                        {isShowClearAllButton && (
                            <Button variant="text" className={classes.button} onClick={handleClearAll}>
                                <LocalizedStrict id="filter-menu-clear-all-button">
                                    <>Clear all</>
                                </LocalizedStrict>
                            </Button>
                        )}
                    </Grid>
                    <Grid>{children}</Grid>
                </Grid>
            </Grid>
        </Drawer>
    )
}

type CustomFilterMenuAccordionProps = {
    title: string
    handleClear: () => void
    counter: number
    children: ReactNode
    expanded?: boolean
    toggleAccordion?: () => void
}

export const CustomFilterMenuAccordion = ({
    title,
    handleClear,
    counter,
    children,
    expanded,
    toggleAccordion,
}: CustomFilterMenuAccordionProps) => {
    const { classes } = useStyles()
    return (
        <Accordion expanded={expanded} onChange={toggleAccordion} className={classes.accordion}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon className={classes.iconColor} />}
                className={classes.accordionSummary}
            >
                <Typography variant="subtitle1" className={classes.accordionTitle}>
                    {title}
                </Typography>
                {!!counter && (
                    <Button
                        variant="text"
                        className={classes.button}
                        onClick={(e) => {
                            e.stopPropagation()
                            handleClear()
                        }}
                    >
                        <Typography variant="subtitle2" className={classes.counter}>
                            {counter}
                        </Typography>
                        <LocalizedStrict id="filter-menu-clear-button">
                            <>Clear</>
                        </LocalizedStrict>
                    </Button>
                )}
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
                <CustomFilterMenuCheckboxGroup>{children}</CustomFilterMenuCheckboxGroup>
            </AccordionDetails>
        </Accordion>
    )
}

type CustomFilterMenuCheckboxGroupProps = {
    children: ReactNode
}

export const CustomFilterMenuCheckboxGroup = ({ children }: CustomFilterMenuCheckboxGroupProps) => (
    <FormControl component="fieldset" fullWidth>
        <FormGroup>{children}</FormGroup>
    </FormControl>
)

type CustomFilterMenuCheckboxProps = {
    optionValue: number
    optionLabel: string
    optionChecked: boolean
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export const CustomFilterMenuCheckbox = ({
    optionValue,
    optionLabel,
    optionChecked,
    onChange,
}: CustomFilterMenuCheckboxProps) => {
    const { classes } = useStyles()
    return (
        <FormControlLabel
            key={optionValue}
            control={
                <Checkbox
                    checked={optionChecked}
                    onChange={onChange}
                    value={optionValue}
                    className={classes.checkbox}
                />
            }
            label={optionLabel}
        />
    )
}
